<template>
  <motlet-neo :options="motletOptions">
    <template v-slot:content="{ props }">
      <line-chart :settings="settings" :motlet="props.motlet" :store-name="storeName" :active-increment="activeIncrement" />
    </template>
  </motlet-neo>
</template>

<script>
import { merge, isEmpty } from 'lodash';
// BEGIN::Added.by.KWShin.2024.09.26.
import bus from '@/utils/bus';
// FINISH::Added.by.KWShin.2024.03.06.
// BEGIN::Added.by.SHJ.2026.04.27.
import { api } from '@/api/common/instances';
// FINISH::Added.by.SHJ.2026.04.27.
import lineChartable from './lineChartable';

export default {
  mixins: [lineChartable],
  data() {
    return {
      motletOptions: {
        id: 'dv/abap/os/disk',
        title: 'dv.title.disk',
        monitor: { monitorCode: 'C_ABAP_OS_DISK', eventCode: 'ABAP_OS_DISK', points: 60 },
        // BEGIN::Modified.by.KWShin.2022.07.13.
        // '표시범위' 기능을 사용하지 않게 되면서 보여줄 것이 없어서 settings는 사용하지 않는다.
        //settings: { chart: { use: true } },
        settings: { chart: { use: false } },
        // FINISH::Modified.by.KWShin.2022.07.13.
      },
      socketData: [],
    };
  },
  watch: {
    sstmId: {
      handler() {
        this.sendMessage();
      },
    },
    activeIncrement: {
      immediate: true,
      handler(value) {
        if (value) this.sendMessage();
      },
    },
  },
  methods: {
    wsBufferedDataHandler(key, responses) {
      const [response] = responses;
      const bound = response.length < this.monitor.points ? response.length : this.monitor.points;
      const data = response.slice(response.length - bound).map(item => {
        return this.dataHandler(item.data, item.ctime, item.empty);
      });
      this.socketData.push({
        id: key.instId,
        data,
      });
      if (this.socketData.length == this.descendants.length) {
        this.data = merge([], this.socketData);
        this.socketData = [];
        this.prepend(['kbSec']);
        // BEGIN::Added.by.KWShin.2024.09.26.
        if (this.data && this.data.length > 0) {
          bus.$emit(`change:timestamp-${this.storeName}`, {
            eventCode: this.motletOptions.monitor.eventCode,
            timestamp: this.data[this.data.length - 1].ctime,
          });
        }
        // FINISH::Added.by.KWShin.2024.09.26.
        // BEGIN::Added.by.SHJ.2026.04.27.
        // WS 버퍼가 차트 X축 개수(monitor.points)만큼 채우지 못한 경우 앞쪽 빈 구간을 DB에서 보충한다.
        this.backfillFromDb();
        // FINISH::Added.by.SHJ.2026.04.27.
      }
    },
    wsDataHandler(key, response, ctime, empty) {
      const data = this.dataHandler(response, ctime, empty);
      this.socketData.push({
        id: key.instId,
        data,
      });
      if (this.socketData.length == this.descendants.length) {
        this.socketData.forEach(s => {
          this.data.find(d => d.id == s.id).data.push(s.data);
        });
        this.socketData = [];
        this.prepop();
      }
    },
    dataHandler(response, ctime, empty) {
      if (isEmpty(response)) response = [{ stime: ctime }];
      const data = {
        ctime: parseInt(ctime) - (parseInt(ctime) % this.monitor.cycle), // db 데이터 간격 기준으로 실시간 데이터를 근사값으로 보정,
        stime: parseInt(response[0].stime),
        kbSec: '',
      };
      if (!empty) {
        data.kbSec = response.reduce((acc, item) => (acc += parseInt(item.kbSec)), 0);
      }
      return data;
    },
    // BEGIN::Added.by.SHJ.2026.04.27.
    /**
     * WS 버퍼가 monitor.points 만큼 채워지지 않았을 때 DB에서 부족한 앞 구간을 조회해
     * 이미 비어있는 슬롯(kbSec===undefined)에만 값을 주입한다. 실제 WS 데이터는 덮어쓰지 않는다.
     */
    async backfillFromDb() {
      if (!this.data || this.data.length === 0) return;
      const firstInst = this.data[0].data;
      if (!firstInst || firstInst.length === 0) return;
      const realPoints = firstInst.filter(d => d.kbSec !== undefined && d.kbSec !== '');
      if (realPoints.length === firstInst.length) return;
      const beginYmdhms = firstInst[0].ctime;
      const endYmdhms = realPoints.length > 0 ? realPoints[0].ctime - this.monitor.cycle : firstInst[firstInst.length - 1].ctime;
      if (endYmdhms < beginYmdhms) return;
      const params = {
        beginYmdhms,
        endYmdhms,
        sstmId: this.sstmId,
        motletCode: this.monitor.monitorCode,
        ...this.dataParam,
      };
      try {
        const { data: results } = await api.get('/motlet-data/chart', { params });
        if (!results || results.length === 0) return;
        results.forEach(result => {
          const inst = this.data.find(d => d.id === result.key.instId);
          if (!inst) return;
          result.data.forEach(db => {
            const dbCtime = parseInt(db.ctime);
            const slot = inst.data.find(p => p.ctime === dbCtime);
            if (slot && (slot.kbSec === undefined || slot.kbSec === '')) {
              slot.kbSec = parseInt(db.kbSec);
              if (db.stime) slot.stime = parseInt(db.stime);
            }
          });
        });
      } catch (e) {
        // DB 조회 실패 시 차트는 WS 실시간 수신으로 점진적으로 채워지므로 무시한다.
        console.warn('[Disk] backfillFromDb failed', e);
      }
    },
    // FINISH::Added.by.SHJ.2026.04.27.
    sendMessage() {
      this.$websocket.realtime.send({
        requests: this.descendants.map(descendant => ({
          key: { name: 'rawOsDisk', sstmId: descendant.systemId, instId: descendant.id },
          fields: ['stime', 'kbSec'],
          handler: {
            buffer: this.wsBufferedDataHandler,
            every: this.wsDataHandler,
          },
        })),
      });
    },
  },
};
</script>
